import React, { useCallback, useEffect, useState } from 'react';
import { PhoneCall } from 'lucide-react';
import { authAPI, callsAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { apiError } from '../../lib/host';
import { ErrorBanner, Modal } from './ui';

/** Phone number the way people read it: +91 98765 43210. */
export function prettyNumber(n) {
  if (!n) return '—';
  const m = /^\+91(\d{5})(\d{5})$/.exec(n);
  return m ? `+91 ${m[1]} ${m[2]}` : n;
}

/** Click-to-call, if the company's plan includes it (Feature Access Control). */
export default function CallDialog(props) {
  const { hasFeature, can } = useTenant();
  if (hasFeature('click_to_call')) return <CallForm {...props} />;
  return (
    <Modal title="Make a call" onClose={props.onClose}
      footer={<button type="button" className="secondary-action" onClick={props.onClose}>Close</button>}>
      <p>Calling from TeleCalX (Click-to-Call) isn't included in your plan.
        {can('billing.manage') ? ' Choose a plan that includes it on Billing & Plan.' : ' Ask your company owner about a plan that includes it.'}</p>
    </Modal>
  );
}

/** Click-to-call. TeleCalX rings the team member's own phone first, then connects the customer. */
function CallForm({ contact, number, onClose, onPlaced }) {
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState(number || contact?.phone || '');
  const [numberId, setNumberId] = useState('');
  const [myPhone, setMyPhone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => callsAPI.telephony().then((res) => setStatus(res.data)).catch((err) => setError(apiError(err, 'Could not check calling status'))),
    [],
  );
  useEffect(() => { load(); }, [load]);

  const saveMyPhone = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await authAPI.updateMe({ phone: myPhone });
      await refresh();
      await load();
    } catch (err) {
      setError(apiError(err, 'Could not save your number'));
    } finally {
      setBusy(false);
    }
  };

  const call = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const body = { ...(contact?.id && { contact_id: contact.id }), ...(to && { to }), ...(numberId && { number_id: Number(numberId) }) };
      const res = await callsAPI.make(body);
      onPlaced(res.data);
    } catch (err) {
      setError(apiError(err, 'Could not start the call'));
      setBusy(false);
    }
  };

  let body;
  let footer = <button className="secondary-action" onClick={onClose}>Close</button>;
  if (!status) {
    body = <p className="panel-empty">{error ? '' : 'Checking calling…'}</p>;
  } else if (!status.configured) {
    body = <div className="call-state"><b>Calling isn't connected yet</b><p>The TeleCalX administrator needs to connect the phone service (Plivo). Contacts, appointments and everything else keep working.</p></div>;
  } else if (status.numbers.length === 0) {
    body = <div className="call-state"><b>Your company doesn't have a phone number yet</b><p>Ask TeleCalX support to assign one. Calls are made from your company number, so customers never see your personal phone.</p></div>;
  } else if (!status.my_phone) {
    body = (
      <form id="my-phone-form" className="form-grid" onSubmit={saveMyPhone}>
        <div className="call-state span-2"><b>Add your mobile number first</b><p>TeleCalX rings this phone, and when you answer it connects you to the customer.</p></div>
        <label className="field span-2">Your mobile number<input type="tel" required value={myPhone} onChange={(e) => setMyPhone(e.target.value)} placeholder="98765 43210" autoFocus /></label>
      </form>
    );
    footer = <>
      <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
      <button type="submit" form="my-phone-form" className="primary-action" disabled={busy}>{busy ? 'Saving…' : 'Save my number'}</button>
    </>;
  } else {
    body = (
      <form id="call-form" className="form-grid" onSubmit={call}>
        <label className="field span-2">{contact ? `Call ${contact.name} on` : 'Number to call'}
          <input type="tel" required={!contact} value={to} onChange={(e) => setTo(e.target.value)} placeholder="98765 43210" autoFocus={!contact} />
        </label>
        {status.numbers.length > 1 && (
          <label className="field span-2">Call from
            <select value={numberId} onChange={(e) => setNumberId(e.target.value)}>
              <option value="">{prettyNumber(status.numbers[0].number)}{status.numbers[0].label ? ` · ${status.numbers[0].label}` : ''}</option>
              {status.numbers.slice(1).map((n) => <option key={n.id} value={n.id}>{prettyNumber(n.number)}{n.label ? ` · ${n.label}` : ''}</option>)}
            </select>
          </label>
        )}
        <div className="call-steps span-2">
          <span><b>1</b>We ring your phone <b>{prettyNumber(status.my_phone)}</b></span>
          <span><b>2</b>When you answer, we connect you to {contact ? contact.name : 'the customer'}</span>
          <span><b>3</b>They see your company number <b>{prettyNumber((status.numbers.find((n) => String(n.id) === numberId) || status.numbers[0]).number)}</b></span>
        </div>
      </form>
    );
    footer = <>
      <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
      <button type="submit" form="call-form" className="primary-action" disabled={busy}><PhoneCall size={14} />{busy ? 'Calling…' : 'Call now'}</button>
    </>;
  }

  return (
    <Modal title={contact ? `Call ${contact.name}` : 'Make a call'} onClose={onClose} footer={footer}>
      <ErrorBanner message={error} />
      {body}
    </Modal>
  );
}
